import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Input } from "@progress/kendo-react-inputs";
import _ from "lodash";
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { kendo, label, number, quote, route, specialCharacter } from "../../../../config";
import { removeExtraSpaces } from "../../../../helper/common";
import { getUserProfileControlsData } from "../../../../shared/services/auth.service";
import { updateProjectNew } from "../../../../shared/services/projects.service";
import { getAllQueues } from "../../../../shared/services/queues.service";
import { joinString } from "../../../../utils";
import { appendString } from "../../../../utils/common";
import { checkRequiredFields, getUpdateProjectPayload, handleProjectDetailsUpdate } from "../../projectSidebar.common";
import { PROJECT_TYPE } from "../../projectSidebar.constants";
import CompanyFeatureSetting from "./CompanyFeatureSetting";
import "../../projectSidebar.scss";

/**
 * ProjectSettings component handles the project configuration settings including name, code, queue, privacy, and other project-specific settings.
 */
const ProjectSettings = (props) => {
  const { isProjectCodeEmpty } = props;
  const [name, setName] = useState("");
  const [nameError, setNameError] = useState(false);
  const [projectCode, setProjectCode] = useState("");
  const [codeError, setCodeError] = useState(false);
  const [dynamicCompanyFeatures, setDynamicCompanyFeatures] = useState([]);
  const [dynamicCompanyFeaturesData, setDynamicCompanyFeaturesData] = useState([]);
  const [projectSettings, setProjectSettings] = useState({
    queue: null,
    privacy: null,
    projectStatus: null,
    costCenter: {},
    businessUnit: {},
  });
  const [defaultSettings, setDefaultSettings] = useState({
    delegateTask: null,
    defaultView: null,
    taskPriority: null,
    dueDateDuration: null,
    archiveAfter: null,
    tags: [],
  });
  const allQueues = useSelector((state) => state.allQueues);
  const projectSetting = useSelector((state) => state.projectSettings);
  const { user } = useSelector((state) => state.auth);
  const { projectDetails, isDisabled, type } = useSelector((state) => state.projectSidebar);
  const { ProjectCode, QueueId, Privacy, ProjectStatus, QueueName, DefaultView, ProjectName , ProjectId} = projectDetails || {};
  const dispatch = useDispatch();
  const history = useHistory();

  /**
   * Redirects to tasks page if project type is personal project
   * @author Bhavana
   */
  useEffect(() => {
    if (type === PROJECT_TYPE.PERSONAL_PROJECT) {
      history.push(route.PRIVATE_ROUTE.TASKS.PATH);
    }
  }, [type]);

  /**
   * Updates project settings when project details or queues change
   * Sets project code, name, queue, privacy, and status
   * @author Bhavana
   */
  useEffect(() => {
    setProjectCode(ProjectCode ? ProjectCode : "");
    setName(ProjectName ? ProjectName : "");

    const settings = { ...projectSettings };
    settings.queue = QueueId ? allQueues?.find((d) => d?.QueueId === QueueId) : null;
    settings.privacy = Privacy
      ? projectSetting?.privacyList?.find((d) => d?.value === Privacy)
      : projectSetting?.privacyList?.find((d) => d?.value === number?.TWO);
    settings.projectStatus = ProjectStatus
      ? projectSetting?.status?.find((d) => d?.value === ProjectStatus)
      : projectSetting?.status?.find((d) => d?.value === number?.TWO);

    setProjectSettings({ ...settings });
  }, [projectDetails, allQueues]);

  /**
   * Updates default settings when project details or project settings change
   * Sets default view for the project
   */
  useEffect(() => {
    const settings = { ...defaultSettings };
    settings.defaultView = DefaultView ? projectSetting?.viewList?.find((d) => d?.value === DefaultView) : null;
    setDefaultSettings({ ...settings });
  }, [projectDetails, projectSetting]);

  /**
   * Fetches profile setting controls data when company ID or project ID changes
   * Updates dynamic company features and their data
   * @author Prachi Jain
   */
  useEffect(() => {
    (async () => {
      if (user?.companyId && ProjectId) {
        const featureListResponse = await getUserProfileControlsData({ companyId: user?.companyId, projectId: ProjectId });
        const { ProfileControls, ...companyFeaturesData } = featureListResponse;
        setDynamicCompanyFeatures(ProfileControls ? ProfileControls : []);
        setDynamicCompanyFeaturesData(companyFeaturesData);
        if (allQueues?.length === number.ZERO) dispatch(getAllQueues());
      }
    })();
  }, [user?.companyId, ProjectId]);

  /**
   * Handles project code input changes and validation
   * @param {Object} event - The input event object
   * @param {string} event.target.name - The name of the input field
   * @param {string} event.target.value - The new value of the input field
   * @author Bhavana
   */
  const handleProjectCode = (event) => {
    let { name, value } = event.target;
    value = joinString(value, "-");
    isProjectCodeEmpty(value);
    if (checkRequiredFields(name, value, setProjectCode, setCodeError)) {
      setProjectCode(value);
      setCodeError(false);
      setProjectSettings({ ...projectSettings, [name]: value });
      debounceInputValue(event);
    }
  };

  /**
   * Handles changes for project settings inputs
   * @param {Object} event - The input event object
   * @param {string} event.target.name - The name of the input field
   * @param {*} event.target.value - The new value of the input field
   * @param {Object} event.target.props - Additional props of the input component
   * @param {string} event.target.props.dataItemKey - Key to access the value from data item
   * @author Bhavana
   */
  const handleChange = (event) => {
    const { name, value, props } = event.target;
    const { dataItemKey } = props;
    const modifiedValue = dataItemKey ? value[dataItemKey] : value;
    handleProjectDetailsUpdate(name, modifiedValue);
    handleProjectUpdate({ [name]: modifiedValue });
    setProjectSettings({ ...projectSettings, [name]: value });
  };

  /**
   * Handles changes for default settings inputs
   * @param {Object} event - The input event object
   * @author Bhavana
   */
  const handleDefaultChange = (event) => {
    const { name, value, props } = event.target;
    const { dataItemKey } = props;

    handleProjectDetailsUpdate(name, dataItemKey ? value[dataItemKey] : value);
    setDefaultSettings({ ...defaultSettings, [name]: value });
  };

  /**
   * used to set value of Project Name
   * @param {event}
   * @author Himanshu Negi
   */
  const handleProjectName = (event) => {
    const { name, value } = event.target;
    if (checkRequiredFields(name, value, setName, setNameError)) {
      setName(value);
      setNameError(false);
      setProjectSettings({ ...projectSettings, [name]: value });
      debounceInputValue(event);
    }
  };

  /**
   * Debounced function to handle input value changes
   * Delays the update of project details to prevent too many API calls
   * @param {Object} event - The input event object
   * @author Bhavana
   */
  const debounceInputValue = useCallback(
    _.debounce(async (event) => {
      const { name, value } = event.target;
      if(value)handleProjectUpdate({ [name]: value });
    }, number.ONE_THOUSAND),
    []
  );

  /**
   * Handles project name blur event
   * Removes extra spaces from the project name
   * @author Bhavana
   */
  const handleProjectNameBlur = () => {
    handleProjectDetailsUpdate("name", removeExtraSpaces(name));
  };

  /**
   * updates projects details and stages
   * @param {void}
   * @return {void}
   * @author Himanshu Negi
   */
  const handleProjectUpdate = (updatedFields) => {
    const payload = getUpdateProjectPayload(updatedFields);
    if (Object.keys(updatedFields).length !== number.ZERO) updateProjectAndDetails(payload);
  };

  /**
   * updates Project and Project Details
   * @param {*} payload
   */
  const updateProjectAndDetails = (payload) => {
    dispatch(updateProjectNew({ userId: user?.id, companyId: user?.companyId, projectId: ProjectId, ...payload }));
  };

  return (
    <>
      <div className='project-settings-container mr-3'>
        <header className='existing-project-header mt-3 ml-3'>
          <Input
            type='text'
            id='project-settings-name'
            placeholder={appendString(user?.operationalTeamName, label?.NAME)}
            required
            autoComplete='off'
            maxLength={kendo?.TEAM_NAME_MAX_LENGTH}
            value={name}
            onChange={handleProjectName}
            disabled={isDisabled}
            name='name'
          />
          {<small className={`text-danger ${!nameError && "invisible"}`}>{quote?.REQUIRED_FIELD}</small>}
        </header>
        <div className='form-row'>
          <div className='form-group col-md-12 d-flex justify-content-between align-items-center'>
            <div className='col-6'>
              <label htmlFor='projectCode'>{label?.TEAM_CODE}</label>
              <span className='text-danger'>{specialCharacter?.STAR}</span>
            </div>
            <Input
              id='projectCode'
              placeholder={appendString(user?.operationalTeamName, label?.CODE)}
              maxLength={number?.TEN}
              value={projectCode?.toUpperCase()}
              onChange={handleProjectCode}
              autoComplete='off'
              name='projectCode'
              onBlur={() => handleProjectDetailsUpdate("projectCode", removeExtraSpaces(projectCode?.toUpperCase()))}
              disabled={isDisabled}
            />
            {<small className={`text-danger ${!codeError && "d-none"}`}>{quote?.REQUIRED_FIELD}</small>}
          </div>
          <div className='form-group col-md-12 d-flex justify-content-between align-items-center'>
            <div className='col-6'>
              <label htmlFor='queueName'>{label?.QUEUE_NAME}</label>
              <span className='text-danger'>{specialCharacter?.STAR}</span>
            </div>
            {isDisabled ? (
              <Input className='form-control' name='queue' value={QueueName} disabled />
            ) : (
              <DropDownList
                id='queueName'
                name='queue'
                data={allQueues?.filter((q) => q?.RoleId)}
                textField='QueueName'
                dataItemKey='QueueId'
                value={projectSettings?.queue}
                onChange={handleChange}
                disabled={isDisabled || projectSettings?.queue?.projectCount === number?.ONE}
              />
            )}
          </div>
        </div>
        <div className='form-row'>
          {dynamicCompanyFeatures?.map((feature) => {
            const { keyName, Name, IsEnabled, IsVisible, ShowSetting } = feature || {};
            const featureValue = dynamicCompanyFeaturesData?.[keyName]?.find((item) => item?.EntityId == projectDetails?.[keyName]);
            return (
              !!IsVisible &&
              !!ShowSetting && (
                <div key={keyName} className='form-group col-md-12 d-flex justify-content-between align-items-center'>
                  <CompanyFeatureSetting
                    title={Name}
                    keyName={keyName}
                    handleChange={handleChange}
                    IsEnabled={IsEnabled}
                    dropdownData={dynamicCompanyFeaturesData?.[keyName]}
                    dropdownValue={featureValue}
                  />
                </div>
              )
            );
          })}
        </div>
        <div className='form-row'>
          <div className='form-group col-md-12 d-flex justify-content-between align-items-center'>
            <label htmlFor='projectStatus' className='col-6'>
              {label?.STATUS}
            </label>
            <DropDownList
              id='projectStatus'
              name='projectStatus'
              data={projectSetting?.status}
              value={projectSettings?.projectStatus}
              onChange={handleChange}
              textField='label'
              dataItemKey='value'
              disabled={isDisabled}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default ProjectSettings;